/**
 * Database-driven feature flags store.
 * Replaces the compile-time VISIBLE_FEATURES object in featureVisibility.ts.
 *
 * Flags are fetched from GET /feature-flags on app load (after auth).
 * Until loaded, all flags default to false (fail closed — no flash of hidden content).
 *
 * Usage in Svelte components:
 *   import { featureFlags } from '$lib/featureFlags.js';
 *   {#if $featureFlags.roundups} ... {/if}
 *
 * Re-loading after admin toggle:
 *   import { loadFeatureFlags } from '$lib/featureFlags.js';
 *   await loadFeatureFlags();
 */

import { writable } from 'svelte/store';
import { api } from '$lib/api.js';

export type FeatureFlagKey =
	| 'subscriptions'
	| 'goals'
	| 'aiRules'
	| 'suggestions'
	| 'roundups'
	| (string & Record<never, never>); // allow unknown keys without breaking narrowing

// All flags default false until loaded (fail closed).
const DEFAULT_FLAGS: Record<string, boolean> = {
	subscriptions: false,
	goals: false,
	aiRules: false,
	suggestions: false,
	roundups: false,
	clientVerifiedPayees: false,
	ccProviderConsent: false,
	// KiwiSaver/IRD move-money destination. Off until Akahu IRD payment consent
	// is established — /payments/ird fails with "No valid payment consent found"
	// without it (see backend/scripts/kiwisaver-ird-payment-probe). Re-enable via admin panel.
	kiwisaverDestination: false,
	// KiwiSaver (IRD) enduring consent enforcement. When ON, a KiwiSaver transfer
	// requires an active STATIC consent for the IRD provider on the funding account.
	// Toggle on together with kiwisaverDestination once Akahu whitelists IRD as STATIC.
	kiwisaverConsent: false,
	// Gates public disposable demo sessions and their landing-page entry point.
	demoMode: false,
};

export const featureFlags = writable<Record<string, boolean>>({ ...DEFAULT_FLAGS });

// In-flight + freshness guard so concurrent callers (e.g. the layout
// `auth.subscribe` firing on init AND again on a token refresh) collapse to a
// single GET /feature-flags instead of issuing a duplicate request each time.
let inflightLoad: Promise<void> | null = null;
let lastLoaded = 0;
const FLAGS_TTL_MS = 60_000;

/**
 * Fetch feature flags from the API and update the store. Call after auth.
 * Deduped: a request already in flight is shared, and a recent successful load
 * (< `FLAGS_TTL_MS`) is skipped. Pass `force: true` to bypass the freshness
 * check (e.g. after an admin toggles a flag).
 */
export async function loadFeatureFlags(force = false): Promise<void> {
	if (inflightLoad) return inflightLoad;
	if (!force && Date.now() - lastLoaded < FLAGS_TTL_MS) return;

	inflightLoad = (async () => {
		try {
			const flags = await api.featureFlags.list();
			// Merge with defaults so unknown flags from DB don't overwrite defaults for missing keys
			featureFlags.set({ ...DEFAULT_FLAGS, ...flags });
			lastLoaded = Date.now();
		} catch {
			// Network error or unauthenticated — keep defaults (all false)
		} finally {
			inflightLoad = null;
		}
	})();

	return inflightLoad;
}

/** Reset flags to defaults on logout. */
export function resetFeatureFlags(): void {
	lastLoaded = 0;
	featureFlags.set({ ...DEFAULT_FLAGS });
}
